import React from "react";
import { Button, ButtonProps, SxProps, Theme } from "@mui/material";

import NewColors from "../themes/NewColors"; // Use NewColors with active theme interface

type StandardButtonVariant =
  | "primary"
  | "secondary"
  | "selected"
  | "success"
  | "warning"
  | "error";
type StandardButtonMode = "contained" | "outlined" | "text";

interface StandardButtonProps
  extends Omit<ButtonProps, "variant" | "color" | "sx"> {
  variant?: StandardButtonVariant;
  mode?: StandardButtonMode;
  testId?: string;
  sx?: SxProps<Theme>;
}

/**
 * Standardized button component to ensure consistent button styling across the app
 */
const StandardButton: React.FC<StandardButtonProps> = ({
  variant = "primary",
  children,
  onClick,
  disabled = false,
  sx = {},
  mode, // Auto-detect mode if not provided
  size = "medium",
  fullWidth = false,
  testId,
  ...otherProps
}) => {
  // Get the active theme colors
  const activeColors = NewColors.active;

  // Auto-detect mode based on variant if not explicitly provided
  const buttonMode =
    mode || (variant === "secondary" ? "outlined" : "contained");

  // Define variant styles using our semantic color system
  const getVariantStyles = () => {
    const isOutlined = buttonMode === "outlined";
    const isText = buttonMode === "text";

    switch (variant) {
      case "primary":
        return {
          backgroundColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : isOutlined || isText
              ? "transparent"
              : activeColors.primaryButton, // Use active theme primary button color
          borderColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : activeColors.primaryButton,
          color: disabled
            ? NewColors.text.muted
            : isOutlined || isText
              ? activeColors.primaryButton
              : NewColors.text.inverse,
          "&:hover": {
            backgroundColor: disabled
              ? NewColors.triadicPower.interactive.disabled
              : isOutlined
                ? NewColors.triadicPower.buttons.primaryHover + "20" // 20% opacity
                : isText
                  ? NewColors.triadicPower.buttons.primaryHover + "10" // 10% opacity
                  : NewColors.triadicPower.buttons.primaryHover,
            borderColor: disabled
              ? NewColors.triadicPower.interactive.disabled
              : NewColors.triadicPower.buttons.primaryHover,
          },
        };

      case "secondary":
        return {
          backgroundColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : isOutlined || isText
              ? "transparent"
              : activeColors.secondaryButton, // Use active theme secondary button
          borderColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : activeColors.primaryButton,
          color: disabled ? NewColors.text.muted : activeColors.primaryButton,
          "&:hover": {
            backgroundColor: disabled
              ? NewColors.triadicPower.interactive.disabled
              : isOutlined
                ? NewColors.triadicPower.buttons.ctaHover + "40" // 40% opacity for subtle effect
                : isText
                  ? NewColors.triadicPower.buttons.ctaHover + "20" // 20% opacity
                  : NewColors.triadicPower.buttons.ctaHover,
            borderColor: disabled
              ? NewColors.triadicPower.interactive.disabled
              : NewColors.triadicPower.buttons.primaryHover,
          },
        };

      case "selected":
        return {
          backgroundColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : isOutlined || isText
              ? "transparent"
              : NewColors.triadicPower.buttons.primary, // Use primary button color as selected base
          borderColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : NewColors.triadicPower.buttons.primary,
          color: disabled ? NewColors.text.muted : NewColors.text.inverse, // White text for better contrast
          "&:hover": {
            backgroundColor: disabled
              ? NewColors.triadicPower.interactive.disabled
              : isOutlined
                ? NewColors.triadicPower.buttons.primaryHover + "20"
                : isText
                  ? NewColors.triadicPower.buttons.primaryHover + "10"
                  : NewColors.triadicPower.buttons.primaryHover, // Darker on hover
            borderColor: disabled ? NewColors.triadicPower.interactive.disabled : NewColors.triadicPower.buttons.primaryHover,
          },
        };

      case "success":
        return {
          backgroundColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : isOutlined || isText
              ? "transparent"
              : NewColors.status.success,
          borderColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : NewColors.status.success,
          color: disabled
            ? NewColors.text.muted
            : isOutlined || isText
              ? NewColors.status.success
              : NewColors.text.inverse,
          "&:hover": {
            backgroundColor: disabled
              ? NewColors.triadicPower.interactive.disabled
              : isOutlined
                ? NewColors.status.success + "20"
                : isText
                  ? NewColors.status.success + "10"
                  : "#2e7d32", // Darker green
            borderColor: disabled ? NewColors.triadicPower.interactive.disabled : "#2e7d32",
          },
        };

      case "warning":
        return {
          backgroundColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : isOutlined || isText
              ? "transparent"
              : NewColors.status.warning,
          borderColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : NewColors.status.warning,
          color: disabled
            ? NewColors.text.muted
            : isOutlined || isText
              ? NewColors.status.warning
              : NewColors.text.inverse,
          "&:hover": {
            backgroundColor: disabled
              ? NewColors.triadicPower.interactive.disabled
              : isOutlined
                ? NewColors.status.warning + "20"
                : isText
                  ? NewColors.status.warning + "10"
                  : "#f57c00", // Darker orange
            borderColor: disabled ? NewColors.triadicPower.interactive.disabled : "#f57c00",
          },
        };

      case "error":
        return {
          backgroundColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : isOutlined || isText
              ? "transparent"
              : NewColors.status.error,
          borderColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : NewColors.status.error,
          color: disabled
            ? NewColors.text.muted
            : isOutlined || isText
              ? NewColors.status.error
              : NewColors.text.inverse,
          "&:hover": {
            backgroundColor: disabled
              ? NewColors.triadicPower.interactive.disabled
              : isOutlined
                ? NewColors.status.error + "20"
                : isText
                  ? NewColors.status.error + "10"
                  : "#c62828", // Darker red
            borderColor: disabled ? NewColors.triadicPower.interactive.disabled : "#c62828",
          },
        };

      default:
        return {
          backgroundColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : isOutlined || isText
              ? "transparent"
              : activeColors.primaryButton,
          borderColor: disabled
            ? NewColors.triadicPower.interactive.disabled
            : activeColors.primaryButton,
          color: disabled
            ? NewColors.text.muted
            : isOutlined || isText
              ? activeColors.primaryButton
              : NewColors.text.inverse,
          "&:hover": {
            backgroundColor: disabled
              ? NewColors.triadicPower.interactive.disabled
              : isOutlined
                ? NewColors.triadicPower.buttons.primaryHover + "20"
                : isText
                  ? NewColors.triadicPower.buttons.primaryHover + "10"
                  : NewColors.triadicPower.buttons.primaryHover,
            borderColor: disabled
              ? NewColors.triadicPower.interactive.disabled
              : NewColors.triadicPower.buttons.primaryHover,
          },
        };
    }
  };

  const variantStyles = getVariantStyles();

  // Standard button styling
  const buttonSx: SxProps<Theme> = {
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 600,
    fontSize:
      size === "small" ? "0.875rem" : size === "large" ? "1rem" : "0.9375rem",
    padding:
      size === "small"
        ? "6px 16px"
        : size === "large"
          ? "10px 24px"
          : "8px 20px",
    minHeight: size === "small" ? "32px" : size === "large" ? "44px" : "38px",
    transition: "all 0.2s ease-in-out",
    ...variantStyles,
    ...sx,
  };

  return (
    <Button
      variant={buttonMode}
      onClick={onClick}
      disabled={disabled}
      sx={buttonSx}
      size={size}
      fullWidth={fullWidth}
      data-testid={testId}
      {...otherProps}
    >
      {children}
    </Button>
  );
};

export default StandardButton;